/**
 * FileUpload Component
 * Reusable file upload component with drag and drop support
 */

import React, { useRef, useState, useEffect } from 'react';
import { useTranslations } from 'next-intl';
import styles from './FileUpload.module.css';

export interface ExistingImage {
  id: string | number;
  url: string;
}

export interface FileUploadProps {
  label?: string;
  accept?: string;
  multiple?: boolean;
  onFileSelect: (files: File[]) => void;
  error?: string;
  helperText?: string;
  disabled?: boolean;
  className?: string;
  maxFiles?: number;
  existingImages?: ExistingImage[];
  onDeleteExisting?: (imageId: string | number) => void;
}

const FileUpload: React.FC<FileUploadProps> = ({
    label,
    accept = 'image/*',
    multiple = true,
    onFileSelect,
    error,
    helperText,
    disabled = false,
    className = '',
    maxFiles = 5,
    existingImages = [],
    onDeleteExisting
}) => {
    const t = useTranslations('fileUpload');
    const fileInputRef = useRef<HTMLInputElement>(null);
    const [isDragOver, setIsDragOver] = useState(false);
    const [selectedFiles, setSelectedFiles] = useState<File[]>([]);
    const [previewUrls, setPreviewUrls] = useState<string[]>([]);

    // Create URL objects for image preview
    useEffect(() => {
        const urls: string[] = [];
        selectedFiles.forEach(file => {
            urls.push(URL.createObjectURL(file));
        });
        setPreviewUrls(urls);

        // Cleanup URL objects on unmount or update
        return () => {
            urls.forEach(url => URL.revokeObjectURL(url));
        };
    }, [selectedFiles]);

    const handleFileChange = (event: React.ChangeEvent<HTMLInputElement>) => {
        const files = event.target.files;
        addFiles(files ? Array.from(files) : []);

        // Reset input to allow selecting the same files again
        if (fileInputRef.current) {
            fileInputRef.current.value = '';
        }
    };

    const addFiles = (newFiles: File[]) => {
        const totalCount = selectedFiles.length + existingImages.length;
        const availableSlots = maxFiles - totalCount;
        if (availableSlots <= 0) return;

        const filesToAdd = newFiles.slice(0, availableSlots);

        // Update state and call callback
        const updatedFiles = [...selectedFiles, ...filesToAdd];
        setSelectedFiles(updatedFiles);
        onFileSelect(updatedFiles);
    };

    const removeFile = (index: number) => {
        const updatedFiles = [...selectedFiles];
        updatedFiles.splice(index, 1);
        setSelectedFiles(updatedFiles);
        onFileSelect(updatedFiles);
    };

    const handleDeleteExisting = (imageId: string | number) => {
        if (onDeleteExisting) {
            onDeleteExisting(imageId);
        }
    };

    const handleDragOver = (event: React.DragEvent) => {
        event.preventDefault();
        if (!disabled) {
            setIsDragOver(true);
        }
    };

    const handleDragLeave = (event: React.DragEvent) => {
        event.preventDefault();
        setIsDragOver(false);
    };

    const handleDrop = (event: React.DragEvent) => {
        event.preventDefault();
        setIsDragOver(false);
    
        if (!disabled) {
            const droppedFiles = event.dataTransfer.files;
            addFiles(Array.from(droppedFiles));
        }
    };

    const handleClick = () => {
        const totalCount = selectedFiles.length + existingImages.length;
        if (!disabled && fileInputRef.current && totalCount < maxFiles) {
            fileInputRef.current.click();
        }
    };

    const containerClasses = [
        styles.container,
        (selectedFiles.length > 0 || existingImages.length > 0) && styles.container_withFiles,
        isDragOver && styles.container__dragOver,
        error && styles.container__error,
        disabled && styles.container__disabled,
        className
    ].filter(Boolean).join(' ');

    const totalImageCount = selectedFiles.length + existingImages.length;
    const canAddMore = totalImageCount < maxFiles;
    const readableFormats = React.useMemo(() => {
        if (!accept) return '';
        return accept
        .split(',')
        .map(type => type.trim())
        .map(type => (type.includes('/') ? type.split('/')[1] : type))
        .map(type => type.replace('*', '').toUpperCase())
        .filter(Boolean).join(', ') || 'Files';
}, [accept]);

    return (
        <div className={styles.wrapper}>
            {label && (
                <label className={styles.label}>
                    {label}
                </label>
            )}
      
            <div
                className={containerClasses}
                onDragOver={handleDragOver}
                onDragLeave={handleDragLeave}
                onDrop={handleDrop}
                onClick={canAddMore ? handleClick : undefined}
            >
                <input
                    ref={fileInputRef}
                    type="file"
                    accept={accept}
                    multiple={multiple}
                    onChange={handleFileChange}
                    disabled={disabled || !canAddMore}
                    className={styles.hiddenInput}
                />
        
                <div className={styles.content}>
                    {(selectedFiles.length > 0 || existingImages.length > 0) ? (
                        <>
                            <div className={styles.thumbnailsContainer}>
                                {existingImages.map((image, index) => (
                                    <div key={`existing-${image.id}`} className={styles.thumbnailWrapper}>
                                        <img
                                            src={image.url}
                                            alt={'Existing image' + index}
                                            className={styles.thumbnail}
                                        />
                                        {onDeleteExisting && (
                                            <div
                                                className={styles.removeButton}
                                                onClick={(e) => {
                                                    e.stopPropagation();
                                                    handleDeleteExisting(image.id);
                                                }}
                                            >
                                                ✕
                                            </div>
                                        )}
                                    </div>
                                ))}

                                {previewUrls.map((url, index) => (
                                    <div key={`new-${index}`} className={styles.thumbnailWrapper}>
                                        <img
                                            src={url}
                                            alt={t('previewAlt', { index: index + 1 })}
                                            className={styles.thumbnail}
                                        />
                                        <div
                                            className={styles.removeButton}
                                            onClick={(e) => {
                                                e.stopPropagation();
                                                removeFile(index);
                                            }}
                                        >
                                            ✕
                                        </div>
                                    </div>
                                ))}

                                {canAddMore && (
                                    <div className={styles.addMoreWrapper}>
                                        <div className={styles.addMoreButton}>+</div>
                                        <div className={styles.addMoreText}>{t('addMore')}</div>
                                    </div>
                                )}
                            </div>

                            <div className={styles.text}>
                                <span className={styles.secondaryText}>
                                    {t('fileCount', { count: totalImageCount, max: maxFiles })}
                                </span>
                            </div>
                        </>
                    ) : (
                        <div className={styles.uploadPrompt}>
                            <div className={styles.icon}>🖼️</div>
                            <div className={styles.text}>
                                <span className={styles.primaryText}>
                                    {t('uploadPrompt')}
                                </span>
                                <span className={styles.secondaryText}>
                                    {accept?.startsWith('image/')
                                    ? t.rich('imageFileTypes', {
                                        max: maxFiles,
                                        fileFormat: () => (
                                            <span className={styles.boldText}>{readableFormats}</span>
                                        ),
                                    })
                                    : t('selectFiles')}
                                </span>
                            </div>
                        </div>
                    )}
                </div>
            </div>
      
            {(error || helperText) && (
                <div className={`${styles.message} ${error ? styles.message__error : ''}`}>
                    {error || helperText}
                </div>
            )}
        </div>
    );
};

export default FileUpload;
